/**
 * Таблица лидеров на клиенте (ТЗ FR-LB-01, FR-LB-06, FR-LB-07).
 *
 * Клиент рейтинг не считает и считать не вправе: место в таблице определяет
 * сервер по засчитанным контрольным результатам. Здесь только то, что нужно,
 * чтобы прочитать готовый снимок и показать его честно — вместе с давностью.
 *
 * Участие — явное действие человека, и по умолчанию его нет (ТЗ FR-LB-06).
 * Хранить согласие только на устройстве было бы обещанием без исполнения:
 * сервер о нём не узнает и в таблицу никого не поставит.
 */

/**
 * Виды таблиц. У каждого своё измерение, и считает их сервер: Progress —
 * основной рейтинг, Skill — подтверждённая скорость, Accuracy — точность
 * лучшего результата, Consistency — регулярность и сохранение навыка,
 * Weekly Growth — рост за неделю.
 */
export const LEADERBOARD_KINDS = [
  'progress',
  'skill',
  'accuracy',
  'consistency',
  'weekly-growth',
] as const;
export type LeaderboardKind = (typeof LEADERBOARD_KINDS)[number];

/**
 * Строка таблицы.
 *
 * Ни адреса, ни настоящего имени, ни расположения: только псевдоним и
 * измеренное (ТЗ FR-LB-06).
 */
export interface LeaderboardRow {
  readonly rank: number;
  readonly displayName: string;
  readonly rating: number;
  readonly verifiedWpm: number;
  readonly accuracy: number;
  /**
   * Величина, по которой построен порядок именно этой таблицы.
   *
   * У Consistency и Weekly Growth её не видно ни в одном другом столбце:
   * без неё их порядок читался бы как случайный.
   */
  readonly metric: number;
  /** Строка смотрящего: отмечается, а не выделяется цветом в одиночку. */
  readonly isYou: boolean;
}

/** Снимок таблицы вместе с тем, что о нём нужно знать смотрящему. */
export interface LeaderboardBoard {
  readonly kind: LeaderboardKind;
  readonly language: string;
  /** Версия формулы рейтинга: таблица без неё несравнима с прошлой. */
  readonly version: string;
  readonly rows: readonly LeaderboardRow[];
  /** Участвует ли смотрящий. Не участвует — его строки в таблице нет. */
  readonly participating: boolean;
  /** Когда снимок посчитан: таблица, молчащая о давности, читается как живая. */
  readonly computedAt: string;
}

/** Настройки участия, которые меняет сам человек. */
export interface LeaderboardParticipation {
  readonly language: string;
  readonly optIn?: boolean;
  /** Псевдоним: в таблице виден он, и ничего кроме него. */
  readonly displayName?: string;
}

/**
 * Порт доставки: сама функция про HTTP не знает.
 *
 * `null` в ответе — «сервер не ответил»: гость, потерянная связь или
 * закрытая сессия. Пустая таблица и отсутствие ответа — разные вещи, и
 * показывать их одинаково нельзя.
 */
export interface LeaderboardGateway {
  board(kind: LeaderboardKind, language: string): Promise<LeaderboardBoard | null>;
  updateParticipation(participation: LeaderboardParticipation): Promise<void>;
}

/**
 * Друг в списке.
 *
 * Ни адреса, ни чужого кода приглашения: код, показанный другу, можно
 * передать дальше, и человека добавит тот, кого он не звал. `linkId`
 * указывает на связь, а не на человека, и вне этого списка не значит ничего.
 */
export interface Friend {
  readonly linkId: string;
  readonly displayName: string;
  readonly addedAt: string;
  /** Участвует ли друг в рейтинге: без согласия его нет и в дружеской таблице. */
  readonly participating: boolean;
}

/** Свой код приглашения и круг друзей. */
export interface FriendsView {
  readonly code: string;
  readonly friends: readonly Friend[];
  readonly maxFriends: number;
}

/** Чем закончилось добавление друга: причину человек должен видеть. */
export type AddFriendOutcome = 'added' | 'not-found' | 'full' | 'failed';

/**
 * Порт дружеского рейтинга.
 *
 * Связывает двоих код приглашения, а не поиск по адресу: сервер намеренно не
 * отвечает, зарегистрирован ли адрес (ТЗ SEC-11), и поиск по нему превратил
 * бы таблицу лидеров в справочник существующих аккаунтов.
 */
export interface FriendsGateway {
  /** Свой код и список друзей; `null` — сервер не ответил. */
  view(): Promise<FriendsView | null>;
  add(code: string): Promise<AddFriendOutcome>;
  remove(linkId: string): Promise<boolean>;
  /** Таблица из себя и друзей; `null` — сервер не ответил. */
  board(language: string): Promise<LeaderboardBoard | null>;
}
